import React from "react";
import { LocaleConfig } from "react-native-calendars";
import isEmpty from "ramda/src/isEmpty";

const LocaleContext = React.createContext({
  messages: {},
  date: {}
});

/**
 * Base class that contains the logic for loading the messages depending on the locale.
 * 
 * Subclasses implement the strategy for loading the message file(s).
 */
class BaseLocaleLoader extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      locale: props.locale,
      messages: {}
    };

    this.switchLocale = this.switchLocale.bind(this);

    this.locales = props.loadLocales();
  }

  /**
   * Ensures that the messages are loaded when the component is first loaded.
   */

  componentDidMount() {
    this.switchLocale(this.props.locale);
  }

  /**
   * Loads messages when the locale changes (user changes from English to Spanish).
   */
  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.locale !== nextProps.locale) {
      this.switchLocale(nextProps.locale);

      //We return false because switchLocale updates state and that'll trigger the needed re-render.
      return false;
    }

    return true;
  }

  /**
   * Set the locale information for react-native-calendars.
   * 
   * We do this because react-native-calendars uses xdate which has an odd way to set up localization.
   * Until this component uses a more modern date library, we must do it this way. https://github.com/wix/react-native-calendars/issues/343
   * 
   * TODO Once this component supports a modern date library, we must remove all dependencies to react-native-calendars from the cci-utility package
   */
  setCalendar({ localize: { day, dayPeriod, month } }) {
    const abbreviated = { width: "abbreviated" };

    LocaleConfig.locales[""] = {
      monthNames: [month(0), month(1), month(2), month(3), month(4), month(5), month(6), month(7), month(8), month(9), month(10), month(11)],
      monthNamesShort: [month(0, abbreviated), month(1, abbreviated), month(2, abbreviated), month(3, abbreviated), month(4, abbreviated), month(5, abbreviated), month(6, abbreviated), month(7, abbreviated), month(8, abbreviated), month(9, abbreviated), month(10, abbreviated), month(11, abbreviated)],
      dayNames: [day(0), day(1), day(2), day(3), day(4), day(5), day(6)],
      dayNamesShort: [day(0, abbreviated), day(1, abbreviated), day(2, abbreviated), day(3, abbreviated), day(4, abbreviated), day(5, abbreviated), day(6, abbreviated)],
      amDesignator: dayPeriod(0),
      pmDesignator: dayPeriod(1)
    };
  }

  render() {
    const { state, props } = this;

    // Component must not be rendered until locale labels are loaded
    if (!state.messages || isEmpty(state.messages)) {
      return null;
    }

    return <LocaleContext.Provider value={state}>{props.children(state.messages)}</LocaleContext.Provider>;
  }
}

export { LocaleContext, BaseLocaleLoader };
